import React, { useState, useEffect, useCallback } from 'react';
import { budgetAPI } from '../services/api';
import BankStatementUpload from './budget/BankStatementUpload';
import SpendingReportPanel from './budget/SpendingReportPanel';
import SpendingChart from './budget/SpendingChart';
import CategoryChart from './budget/CategoryChart';
import MonthlySummary from './budget/MonthlySummary';
import './BudgetTab.css';

const BudgetTab = ({ houseId }) => {
  const [activeSection, setActiveSection] = useState('summary');
  const [loading, setLoading] = useState(false);
  
  // Budget Items (Charges)
  const [budgetItems, setBudgetItems] = useState([]);
  const [showBudgetItemForm, setShowBudgetItemForm] = useState(false);
  const [editingBudgetItem, setEditingBudgetItem] = useState(null);
  const [budgetItemForm, setBudgetItemForm] = useState({
    label: '',
    amount: '',
    withdrawal_day: '',
    is_active: true,
  });

  // Loans
  const [loans, setLoans] = useState([]);
  const [showLoanForm, setShowLoanForm] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [loanForm, setLoanForm] = useState({
    label: '',
    amount: '',
    is_active: true,
    person_ids: [], // Array of selected person IDs
  });

  // People
  const [people, setPeople] = useState([]);
  const [showPersonForm, setShowPersonForm] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [personForm, setPersonForm] = useState({
    name: '',
    salary: '',
    charge_percentage: '',
    loan_percentage: '',
  });

  // Savings
  const [savings, setSavings] = useState([]);
  const [showSavingsForm, setShowSavingsForm] = useState(false);
  const [editingSavings, setEditingSavings] = useState(null);
  const [savingsForm, setSavingsForm] = useState({
    name: '',
    monthly_amount: '',
    yearly_amount: '',
    is_active: true,
  });

  // Transactions (read-only, from bank statements)
  const [transactions, setTransactions] = useState([]);
  const [categorizing, setCategorizing] = useState(false);
  const [updatingTransaction, setUpdatingTransaction] = useState(null);
  
  // Bank Statements and Analysis
  const [bankStatements, setBankStatements] = useState([]);
  const [spendingData, setSpendingData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState({
    spending: false,
    category: false,
    monthly: false,
  });
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('upload');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(null);

  // Summary
  const [summary, setSummary] = useState(null);

  // Report
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDates, setReportDates] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    loadAllData();
  }, [houseId, activeSection]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadBudgetItems(),
        loadLoans(),
        loadPeople(),
        loadSavings(),
        loadTransactions(),
        loadSummary(),
        loadBankStatements(),
      ]);
    } catch (error) {
      console.error('Error loading budget data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBudgetItems = async () => {
    try {
      const response = await budgetAPI.getBudgetItems(houseId, true);
      setBudgetItems(response.data);
    } catch (error) {
      console.error('Error loading budget items:', error);
    }
  };

  const loadLoans = async () => {
    try {
      const response = await budgetAPI.getLoans(houseId, true);
      setLoans(response.data);
    } catch (error) {
      console.error('Error loading loans:', error);
    }
  };

  const loadPeople = async () => {
    try {
      const response = await budgetAPI.getBudgetPeople(houseId);
      setPeople(response.data);
    } catch (error) {
      console.error('Error loading people:', error);
    }
  };

  const loadSavings = async () => {
    try {
      const response = await budgetAPI.getSavingsCategories(houseId, true);
      setSavings(response.data);
    } catch (error) {
      console.error('Error loading savings:', error);
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await budgetAPI.getTransactions(houseId);
      setTransactions(response.data);
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  };

  const loadBankStatements = async () => {
    try {
      const response = await budgetAPI.getBankStatements(houseId);
      setBankStatements(response.data);
    } catch (error) {
      console.error('Error loading bank statements:', error);
    }
  };

  const handleUploadComplete = () => {
    // Reload data after upload
    loadTransactions();
    loadBankStatements();
    // Auto-load analysis if we're on analysis tab
    if (activeSection === 'transactions' && activeAnalysisTab !== 'upload') {
      loadAnalysisData();
    }
  };

  const loadAnalysisData = useCallback(async () => {
    if (!houseId) return;

    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 6);
    const startDateStr = startDate.toISOString().split('T')[0];
    const currentYear = new Date().getFullYear();

    // Load spending over time
    setAnalysisLoading(prev => ({ ...prev, spending: true }));
    try {
      const response = await budgetAPI.getSpendingOverTime(houseId, startDateStr, endDate);
      setSpendingData(response.data);
    } catch (error) {
      console.error('Error loading spending data:', error);
    } finally {
      setAnalysisLoading(prev => ({ ...prev, spending: false }));
    }

    // Load category breakdown
    setAnalysisLoading(prev => ({ ...prev, category: true }));
    try {
      const response = await budgetAPI.getCategoryBreakdown(houseId, startDateStr, endDate);
      setCategoryData(response.data);
    } catch (error) {
      console.error('Error loading category data:', error);
    } finally {
      setAnalysisLoading(prev => ({ ...prev, category: false }));
    }

    // Load monthly summary
    setAnalysisLoading(prev => ({ ...prev, monthly: true }));
    try {
      const response = await budgetAPI.getMonthlySummary(houseId, currentYear);
      setMonthlyData(response.data);
    } catch (error) {
      console.error('Error loading monthly data:', error);
    } finally {
      setAnalysisLoading(prev => ({ ...prev, monthly: false }));
    }

  }, [houseId]);

  useEffect(() => {
    if (activeSection === 'transactions' && activeAnalysisTab !== 'upload') {
      loadAnalysisData();
    }
  }, [activeSection, activeAnalysisTab, loadAnalysisData]);

  const loadSummary = async () => {
    try {
      const response = await budgetAPI.getSummary(houseId);
      setSummary(response.data);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const loadReport = async () => {
    if (!reportDates.start_date || !reportDates.end_date) {
      alert('Please select both start and end dates');
      return;
    }
    
    try {
      setReportLoading(true);
      const response = await budgetAPI.getReport(
        reportDates.start_date,
        reportDates.end_date,
        houseId
      );
      setReport(response.data);
    } catch (error) {
      console.error('Error loading report:', error);
      alert('Error loading report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setReportLoading(false);
    }
  };

  // Budget Items handlers
  const handleBudgetItemSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...budgetItemForm,
        amount: parseFloat(budgetItemForm.amount),
        withdrawal_day: budgetItemForm.withdrawal_day ? parseInt(budgetItemForm.withdrawal_day) : null,
        house_id: houseId || null,
      };
      
      if (editingBudgetItem) {
        await budgetAPI.updateBudgetItem(editingBudgetItem.id, data);
      } else {
        await budgetAPI.createBudgetItem(data);
      }
      
      resetBudgetItemForm();
      loadAllData();
    } catch (error) {
      console.error('Error saving budget item:', error);
      alert('Error saving budget item: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditBudgetItem = (item) => {
    setEditingBudgetItem(item);
    setBudgetItemForm({
      label: item.label,
      amount: item.amount.toString(),
      withdrawal_day: item.withdrawal_day?.toString() || '',
      is_active: item.is_active,
    });
    setShowBudgetItemForm(true);
  };

  const handleDeleteBudgetItem = async (id) => {
    if (!window.confirm('Delete this budget item?')) return;
    try {
      await budgetAPI.deleteBudgetItem(id);
      loadAllData();
    } catch (error) {
      console.error('Error deleting budget item:', error);
      alert('Error deleting budget item');
    }
  };

  const resetBudgetItemForm = () => {
    setBudgetItemForm({
      label: '',
      amount: '',
      withdrawal_day: '',
      is_active: true,
    });
    setEditingBudgetItem(null);
    setShowBudgetItemForm(false);
  };

  // Loans handlers
  const handleLoanSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        label: loanForm.label,
        amount: parseFloat(loanForm.amount),
        is_active: loanForm.is_active,
        house_id: houseId || null,
        person_ids: loanForm.person_ids || [],
      };
      
      if (editingLoan) {
        await budgetAPI.updateLoan(editingLoan.id, data);
      } else {
        await budgetAPI.createLoan(data);
      }
      
      resetLoanForm();
      loadAllData();
    } catch (error) {
      console.error('Error saving loan:', error);
      alert('Error saving loan: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditLoan = (loan) => {
    setEditingLoan(loan);
    setLoanForm({
      label: loan.label,
      amount: loan.amount.toString(),
      is_active: loan.is_active,
      person_ids: loan.person_ids || [],
    });
    setShowLoanForm(true);
  };

  const handleDeleteLoan = async (id) => {
    if (!window.confirm('Delete this loan?')) return;
    try {
      await budgetAPI.deleteLoan(id);
      loadAllData();
    } catch (error) {
      console.error('Error deleting loan:', error);
      alert('Error deleting loan');
    }
  };

  const resetLoanForm = () => {
    setLoanForm({
      label: '',
      amount: '',
      is_active: true,
      person_ids: [],
    });
    setEditingLoan(null);
    setShowLoanForm(false);
  };

  const handlePersonToggle = (personId) => {
    const currentIds = loanForm.person_ids || [];
    if (currentIds.includes(personId)) {
      setLoanForm({
        ...loanForm,
        person_ids: currentIds.filter(id => id !== personId),
      });
    } else {
      setLoanForm({
        ...loanForm,
        person_ids: [...currentIds, personId],
      });
    }
  };

  // People handlers
  const handlePersonSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...personForm,
        salary: parseFloat(personForm.salary),
        charge_percentage: parseFloat(personForm.charge_percentage),
        loan_percentage: parseFloat(personForm.loan_percentage),
        house_id: houseId || null,
      };
      
      if (editingPerson) {
        await budgetAPI.updateBudgetPerson(editingPerson.id, data);
      } else {
        await budgetAPI.createBudgetPerson(data);
      }
      
      resetPersonForm();
      loadAllData();
    } catch (error) {
      console.error('Error saving person:', error);
      alert('Error saving person: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditPerson = (person) => {
    setEditingPerson(person);
    setPersonForm({
      name: person.name,
      salary: person.salary.toString(),
      charge_percentage: person.charge_percentage.toString(),
      loan_percentage: person.loan_percentage.toString(),
    });
    setShowPersonForm(true);
  };

  const handleDeletePerson = async (id) => {
    if (!window.confirm('Delete this person?')) return;
    try {
      await budgetAPI.deleteBudgetPerson(id);
      loadAllData();
    } catch (error) {
      console.error('Error deleting person:', error);
      alert('Error deleting person');
    }
  };

  const resetPersonForm = () => {
    setPersonForm({
      name: '',
      salary: '',
      charge_percentage: '',
      loan_percentage: '',
    });
    setEditingPerson(null);
    setShowPersonForm(false);
  };

  // Savings handlers
  const handleSavingsSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...savingsForm,
        monthly_amount: parseFloat(savingsForm.monthly_amount),
        yearly_amount: parseFloat(savingsForm.yearly_amount),
        house_id: houseId || null,
      };
      
      if (editingSavings) {
        await budgetAPI.updateSavingsCategory(editingSavings.id, data);
      } else {
        await budgetAPI.createSavingsCategory(data);
      }
      
      resetSavingsForm();
      loadAllData();
    } catch (error) {
      console.error('Error saving savings category:', error);
      alert('Error saving savings category: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditSavings = (saving) => {
    setEditingSavings(saving);
    setSavingsForm({
      name: saving.name,
      monthly_amount: saving.monthly_amount.toString(),
      yearly_amount: saving.yearly_amount.toString(),
      is_active: saving.is_active,
    });
    setShowSavingsForm(true);
  };

  const handleDeleteSavings = async (id) => {
    if (!window.confirm('Delete this savings category?')) return;
    try {
      await budgetAPI.deleteSavingsCategory(id);
      loadAllData();
    } catch (error) {
      console.error('Error deleting savings category:', error);
      alert('Error deleting savings category');
    }
  };

  const resetSavingsForm = () => {
    setSavingsForm({
      name: '',
      monthly_amount: '',
      yearly_amount: '',
      is_active: true,
    });
    setEditingSavings(null);
    setShowSavingsForm(false);
  };

  // Transaction handlers removed - transactions are now auto-created from bank statements

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (loading && !summary) {
    return <div className="budget-tab-loading">Loading budget data...</div>;
  }

  return (
    <div className="budget-tab">
      <div className="budget-header">
        <h2>💰 Budget Management</h2>
      </div>

      <div className="budget-sections">
        <button
          className={`section-button ${activeSection === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveSection('summary')}
        >
          Summary
        </button>
        <button
          className={`section-button ${activeSection === 'charges' ? 'active' : ''}`}
          onClick={() => setActiveSection('charges')}
        >
          Fixed Charges
        </button>
        <button
          className={`section-button ${activeSection === 'loans' ? 'active' : ''}`}
          onClick={() => setActiveSection('loans')}
        >
          Loans
        </button>
        <button
          className={`section-button ${activeSection === 'people' ? 'active' : ''}`}
          onClick={() => setActiveSection('people')}
        >
          People
        </button>
        <button
          className={`section-button ${activeSection === 'savings' ? 'active' : ''}`}
          onClick={() => setActiveSection('savings')}
        >
          Savings
        </button>
        <button
          className={`section-button ${activeSection === 'transactions' ? 'active' : ''}`}
          onClick={() => {
            setActiveSection('transactions');
            if (activeAnalysisTab === 'upload') {
              setActiveAnalysisTab('upload');
            }
          }}
        >
          Analysis
        </button>
        <button
          className={`section-button ${activeSection === 'report' ? 'active' : ''}`}
          onClick={() => setActiveSection('report')}
        >
          Report
        </button>
      </div>

      {/* Fixed Charges Section */}
      {activeSection === 'charges' && (
        <div className="budget-section">
          <div className="section-header">
            <h3>Fixed Monthly Charges</h3>
            <button className="btn-add" onClick={() => setShowBudgetItemForm(true)}>
              + Add Charge
            </button>
          </div>

          {showBudgetItemForm && (
            <form onSubmit={handleBudgetItemSubmit} className="budget-form">
              <input
                type="text"
                placeholder="Label *"
                value={budgetItemForm.label}
                onChange={(e) => setBudgetItemForm({ ...budgetItemForm, label: e.target.value })}
                required
              />
              <input
                type="number"
                step="0.01"
                placeholder="Amount (€) *"
                value={budgetItemForm.amount}
                onChange={(e) => setBudgetItemForm({ ...budgetItemForm, amount: e.target.value })}
                required
              />
              <input
                type="number"
                min="1"
                max="31"
                placeholder="Withdrawal Day (1-31, optional)"
                value={budgetItemForm.withdrawal_day}
                onChange={(e) => setBudgetItemForm({ ...budgetItemForm, withdrawal_day: e.target.value })}
              />
              <label>
                <input
                  type="checkbox"
                  checked={budgetItemForm.is_active}
                  onChange={(e) => setBudgetItemForm({ ...budgetItemForm, is_active: e.target.checked })}
                />
                Active
              </label>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={resetBudgetItemForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingBudgetItem ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          )}

          <div className="items-list">
            {budgetItems.map((item) => (
              <div key={item.id} className="budget-item-card">
                <div className="item-info">
                  <div className="item-label">{item.label}</div>
                  <div className="item-amount">{formatCurrency(item.amount)}</div>
                  {item.withdrawal_day && (
                    <div className="item-day">Day: {item.withdrawal_day}</div>
                  )}
                  {!item.is_active && <span className="inactive-badge">Inactive</span>}
                </div>
                <div className="item-actions">
                  <button className="btn-edit" onClick={() => handleEditBudgetItem(item)}>
                    Edit
                  </button>
                  <button className="btn-delete" onClick={() => handleDeleteBudgetItem(item.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
            {budgetItems.length === 0 && (
              <div className="empty-state">No fixed charges yet. Add your first charge!</div>
            )}
          </div>
        </div>
      )}

      {/* Loans Section */}
      {activeSection === 'loans' && (
        <div className="budget-section">
          <div className="section-header">
            <h3>Loans</h3>
            <button className="btn-add" onClick={() => setShowLoanForm(true)}>
              + Add Loan
            </button>
          </div>

          {showLoanForm && (
            <form onSubmit={handleLoanSubmit} className="budget-form">
              <input
                type="text"
                placeholder="Label *"
                value={loanForm.label}
                onChange={(e) => setLoanForm({ ...loanForm, label: e.target.value })}
                required
              />
              <input
                type="number"
                step="0.01"
                placeholder="Amount (€) *"
                value={loanForm.amount}
                onChange={(e) => setLoanForm({ ...loanForm, amount: e.target.value })}
                required
              />
              <label>
                <input
                  type="checkbox"
                  checked={loanForm.is_active}
                  onChange={(e) => setLoanForm({ ...loanForm, is_active: e.target.checked })}
                />
                Active
              </label>
              
              {/* Person Selection */}
              {people.length > 0 && (
                <div className="person-selection">
                  <label className="form-label">Select People Participating in This Loan:</label>
                  <div className="person-checkboxes">
                    {people.map((person) => (
                      <label key={person.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={(loanForm.person_ids || []).includes(person.id)}
                          onChange={() => handlePersonToggle(person.id)}
                        />
                        <span>{person.name}</span>
                      </label>
                    ))}
                  </div>
                  <small className="form-hint">
                    Loan amount will be split equally among selected people. If no people are selected, the old loan percentage method will be used.
                  </small>
                </div>
              )}
              
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={resetLoanForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingLoan ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          )}

          <div className="items-list">
            {loans.map((loan) => (
              <div key={loan.id} className="budget-item-card">
                <div className="item-info">
                  <div className="item-label">{loan.label}</div>
                  <div className="item-amount">{formatCurrency(loan.amount)}</div>
                  {!loan.is_active && <span className="inactive-badge">Inactive</span>}
                </div>
                <div className="item-actions">
                  <button className="btn-edit" onClick={() => handleEditLoan(loan)}>
                    Edit
                  </button>
                  <button className="btn-delete" onClick={() => handleDeleteLoan(loan.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
            {loans.length === 0 && (
              <div className="empty-state">No loans yet. Add your first loan!</div>
            )}
          </div>
        </div>
      )}

      {/* People Section */}
      {activeSection === 'people' && (
        <div className="budget-section">
          <div className="section-header">
            <h3>People & Salary Distribution</h3>
            <button className="btn-add" onClick={() => setShowPersonForm(true)}>
              + Add Person
            </button>
          </div>

          {showPersonForm && (
            <form onSubmit={handlePersonSubmit} className="budget-form">
              <input
                type="text"
                placeholder="Name *"
                value={personForm.name}
                onChange={(e) => setPersonForm({ ...personForm, name: e.target.value })}
                required
              />
              <input
                type="number"
                step="0.01"
                placeholder="Salary (€) *"
                value={personForm.salary}
                onChange={(e) => setPersonForm({ ...personForm, salary: e.target.value })}
                required
              />
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="Charge Percentage (%) *"
                value={personForm.charge_percentage}
                onChange={(e) => setPersonForm({ ...personForm, charge_percentage: e.target.value })}
                required
              />
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="Loan Percentage (%) *"
                value={personForm.loan_percentage}
                onChange={(e) => setPersonForm({ ...personForm, loan_percentage: e.target.value })}
                required
              />
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={resetPersonForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingPerson ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          )}

          <div className="items-list">
            {people.map((person) => (
              <div key={person.id} className="budget-item-card">
                <div className="item-info">
                  <div className="item-label">{person.name}</div>
                  <div className="item-details">
                    <div>Salary: {formatCurrency(person.salary)}</div>
                    <div>Charges: {person.charge_percentage}%</div>
                    <div>Loans: {person.loan_percentage}%</div>
                  </div>
                </div>
                <div className="item-actions">
                  <button className="btn-edit" onClick={() => handleEditPerson(person)}>
                    Edit
                  </button>
                  <button className="btn-delete" onClick={() => handleDeletePerson(person.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
            {people.length === 0 && (
              <div className="empty-state">No people yet. Add the first person!</div>
            )}
          </div>
        </div>
      )}

      {/* Savings Section */}
      {activeSection === 'savings' && (
        <div className="budget-section">
          <div className="section-header">
            <h3>Savings Categories</h3>
            <button className="btn-add" onClick={() => setShowSavingsForm(true)}>
              + Add Savings
            </button>
          </div>

          {showSavingsForm && (
            <form onSubmit={handleSavingsSubmit} className="budget-form">
              <input
                type="text"
                placeholder="Name *"
                value={savingsForm.name}
                onChange={(e) => setSavingsForm({ ...savingsForm, name: e.target.value })}
                required
              />
              <input
                type="number"
                step="0.01"
                placeholder="Monthly Amount (€) *"
                value={savingsForm.monthly_amount}
                onChange={(e) => setSavingsForm({ ...savingsForm, monthly_amount: e.target.value })}
                required
              />
              <input
                type="number"
                step="0.01"
                placeholder="Yearly Amount (€) *"
                value={savingsForm.yearly_amount}
                onChange={(e) => setSavingsForm({ ...savingsForm, yearly_amount: e.target.value })}
                required
              />
              <label>
                <input
                  type="checkbox"
                  checked={savingsForm.is_active}
                  onChange={(e) => setSavingsForm({ ...savingsForm, is_active: e.target.checked })}
                />
                Active
              </label>
              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={resetSavingsForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingSavings ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          )}

          <div className="items-list">
            {savings.map((saving) => (
              <div key={saving.id} className="budget-item-card">
                <div className="item-info">
                  <div className="item-label">{saving.name}</div>
                  <div className="item-details">
                    <div>Monthly: {formatCurrency(saving.monthly_amount)}</div>
                    <div>Yearly: {formatCurrency(saving.yearly_amount)}</div>
                  </div>
                  {!saving.is_active && <span className="inactive-badge">Inactive</span>}
                </div>
                <div className="item-actions">
                  <button className="btn-edit" onClick={() => handleEditSavings(saving)}>
                    Edit
                  </button>
                  <button className="btn-delete" onClick={() => handleDeleteSavings(saving.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
            {savings.length === 0 && (
              <div className="empty-state">No savings categories yet. Add your first savings goal!</div>
            )}
          </div>
        </div>
      )}

      {/* Bank Statements & Analysis Section */}
      {activeSection === 'transactions' && (
        <div className="budget-section">
          <div className="analysis-tabs">
            <button
              className={`analysis-tab ${activeAnalysisTab === 'upload' ? 'active' : ''}`}
              onClick={() => setActiveAnalysisTab('upload')}
            >
              Upload Statements
            </button>
            <button
              className={`analysis-tab ${activeAnalysisTab === 'spending' ? 'active' : ''}`}
              onClick={() => {
                setActiveAnalysisTab('spending');
                if (!spendingData && !analysisLoading.spending) {
                  loadAnalysisData();
                }
              }}
            >
              Spending Trends
            </button>
            <button
              className={`analysis-tab ${activeAnalysisTab === 'category' ? 'active' : ''}`}
              onClick={() => {
                setActiveAnalysisTab('category');
                if (!categoryData && !analysisLoading.category) {
                  loadAnalysisData();
                }
                if (transactions.length === 0) {
                  loadTransactions();
                }
                // Reload budget items to ensure we have all items (including global ones)
                loadBudgetItems();
              }}
            >
              Category & Transactions
            </button>
            <button
              className={`analysis-tab ${activeAnalysisTab === 'monthly' ? 'active' : ''}`}
              onClick={() => {
                setActiveAnalysisTab('monthly');
                if (!monthlyData && !analysisLoading.monthly) {
                  loadAnalysisData();
                }
              }}
            >
              Monthly Summary
            </button>
          </div>

          {activeAnalysisTab === 'upload' && (
            <div className="analysis-content">
              <div className="section-header">
                <h3>Upload Bank Statements</h3>
                <p className="section-description">
                  Upload PDF bank statements (LCL or BoursoBank) to extract transactions.
                  Then generate the full spending analysis report below.
                </p>
              </div>
              <BankStatementUpload houseId={houseId} onUploadComplete={handleUploadComplete} />

              <SpendingReportPanel houseId={houseId} statementCount={bankStatements.length} />
              
              {bankStatements.length > 0 && (
                <div className="bank-statements-list">
                  <h4>Uploaded Statements ({bankStatements.length})</h4>
                  {bankStatements.map((statement) => (
                    <div key={statement.id} className="statement-item">
                      <div className="statement-info">
                        <div className="statement-file-name">{statement.file_name}</div>
                        <div className="statement-meta">
                          {statement.account_name && <span>{statement.account_name}</span>}
                          {statement.account_number && <span>Account: {statement.account_number}</span>}
                          {statement.statement_date && (
                            <span>Date: {new Date(statement.statement_date).toLocaleDateString('fr-FR')}</span>
                          )}
                          <span>Transactions: {statement.transactions_count}</span>
                          <span>Uploaded: {new Date(statement.uploaded_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                      <button
                        className="btn-delete-small"
                        onClick={async () => {
                          if (window.confirm('Delete this bank statement and all associated transactions?')) {
                            try {
                              await budgetAPI.deleteBankStatement(statement.id);
                              handleUploadComplete();
                            } catch (error) {
                              console.error('Error deleting statement:', error);
                              alert('Error deleting statement: ' + (error.response?.data?.detail || error.message));
                            }
                          }
                        }}
                        title="Delete statement"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeAnalysisTab === 'spending' && (
            <div className="analysis-content">
              <SpendingChart data={spendingData} loading={analysisLoading.spending} />
            </div>
          )}

          {activeAnalysisTab === 'category' && (
            <div className="analysis-content">
              <div className="category-transactions-section">
                <div className="category-chart-section">
                  <CategoryChart 
                    data={categoryData} 
                    loading={analysisLoading.category}
                    onCategoryClick={(categoryName) => {
                      // If clicking the same category, clear filter; otherwise, filter by that category
                      if (selectedCategoryFilter === categoryName) {
                        setSelectedCategoryFilter(null);
                      } else {
                        setSelectedCategoryFilter(categoryName);
                      }
                    }}
                    selectedCategory={selectedCategoryFilter}
                  />
                  {selectedCategoryFilter && (
                    <div className="category-filter-info">
                      <span>Filtered by: <strong>{selectedCategoryFilter}</strong></span>
                      <button 
                        className="btn-small"
                        onClick={() => setSelectedCategoryFilter(null)}
                      >
                        Clear Filter
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="transactions-section">
                  <div className="section-header">
                    <h3>Transaction List</h3>
                    <p className="section-description">
                      {selectedCategoryFilter 
                        ? `Transactions filtered by: ${selectedCategoryFilter}`
                        : 'All transactions extracted from bank statements. Internal transfers between accounts are automatically excluded from analysis charts.'
                      }
                    </p>
                    <div className="action-buttons" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        className="btn-submit"
                        onClick={async () => {
                          try {
                            setCategorizing(true);
                            await budgetAPI.createDefaultBudgetItems(houseId);
                            await loadBudgetItems();
                            alert('Default budget items created successfully!');
                          } catch (error) {
                            console.error('Error creating default budget items:', error);
                            alert('Error creating default budget items: ' + (error.response?.data?.detail || error.message));
                          } finally {
                            setCategorizing(false);
                          }
                        }}
                        disabled={categorizing || !houseId}
                      >
                        {categorizing ? 'Creating...' : 'Create Default Categories'}
                      </button>
                      <button
                        className="btn-submit"
                        onClick={async () => {
                          if (!window.confirm('Re-categorize all uncategorized transactions? This will run the categorization algorithm on all transactions without categories.')) {
                            return;
                          }
                          try {
                            setCategorizing(true);
                            const response = await budgetAPI.reCategorizeTransactions(houseId, false);
                            await loadTransactions();
                            await loadBudgetItems();
                            await loadAnalysisData(); // Reload category data
                            alert(`Re-categorization complete! ${response.data.categorized} transactions categorized, ${response.data.skipped} skipped.`);
                          } catch (error) {
                            console.error('Error re-categorizing transactions:', error);
                            alert('Error re-categorizing: ' + (error.response?.data?.detail || error.message));
                          } finally {
                            setCategorizing(false);
                          }
                        }}
                        disabled={categorizing || !houseId}
                      >
                        {categorizing ? 'Categorizing...' : 'Re-categorize All'}
                      </button>
                    </div>
                  </div>
                  <div className="transactions-list">
                {(() => {
                  // Filter transactions based on selected category
                  // Normalize strings for comparison (case-insensitive, handle accents)
                  const normalizeString = (str) => {
                    if (!str) return '';
                    // Convert to lowercase, trim, remove accents, normalize spaces
                    return str
                      .toLowerCase()
                      .trim()
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
                      .replace(/\s+/g, ' '); // Normalize spaces
                  };
                  
                  const filteredTransactions = transactions.filter((transaction) => {
                    if (!selectedCategoryFilter) return true;
                    
                    const filterNormalized = normalizeString(selectedCategoryFilter);
                    
                    // Handle "Non catégorisé" / "Uncategorized" filter
                    const uncatVariants = [
                      normalizeString('Non catégorisé'),
                      normalizeString('Uncategorized'),
                      normalizeString('Non categorisé'),
                      'non categorise',
                      'uncategorized',
                      'non categorisé'
                    ];
                    if (uncatVariants.includes(filterNormalized)) {
                      return !transaction.budget_item_id;
                    }
                    
                    // Filter by category name (budget item label) - exact match with normalization
                    if (!transaction.budget_item_id) return false;
                    
                    // Find the budget item for this transaction
                    const budgetItem = budgetItems.find(bi => bi.id === transaction.budget_item_id);
                    if (!budgetItem || !budgetItem.label) {
                      // Transaction has budget_item_id but item not found in our list
                      return false;
                    }
                    
                    // Normalize both strings and compare
                    const itemLabelNormalized = normalizeString(budgetItem.label);
                    return itemLabelNormalized === filterNormalized;
                  });

                  if (filteredTransactions.length === 0) {
                    return (
                      <div className="empty-state">
                        {selectedCategoryFilter 
                          ? `No transactions found for category: ${selectedCategoryFilter}`
                          : 'No transactions yet. Upload bank statements to extract transactions!'
                        }
                      </div>
                    );
                  }

                  return filteredTransactions.map((transaction) => {
                  const isIncome = transaction.transaction_type === 'income';
                  const budgetItem = transaction.budget_item_id 
                    ? budgetItems.find(bi => bi.id === transaction.budget_item_id)
                    : null;
                  const isUpdating = updatingTransaction === transaction.id;
                  
                  // Check if this is a transfer (for UI indication)
                  const isTransfer = transaction.description && (
                    transaction.description.toLowerCase().includes('virement') ||
                    transaction.description.toLowerCase().includes('vir ') ||
                    transaction.description.toLowerCase().startsWith('vir') ||
                    transaction.description.toLowerCase().includes('transfer')
                  );

                  return (
                    <div key={transaction.id} className={`transaction-card ${isIncome ? 'income' : 'expense'} ${isTransfer ? 'transfer-transaction' : ''}`}>
                      <div className="transaction-icon">
                        {isTransfer ? (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 5v14M5 12h14"/>
                          </svg>
                        ) : isIncome ? (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 5v14M5 12l7-7 7 7"/>
                          </svg>
                        ) : (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 19V5M5 12l7 7 7-7"/>
                          </svg>
                        )}
                      </div>
                      <div className="transaction-content">
                        <div className="transaction-header">
                          <div className="transaction-description">
                            {transaction.description}
                            {isTransfer && (
                              <span className="transfer-badge" title="Internal transfer - excluded from analysis charts">
                                (Transfer)
                              </span>
                            )}
                          </div>
                          <div className={`transaction-amount ${isIncome ? 'income' : 'expense'}`}>
                            {isIncome ? '+' : '-'}{formatCurrency(transaction.amount)}
                          </div>
                        </div>
                        <div className="transaction-meta">
                          <span className="transaction-date">
                            {new Date(transaction.date).toLocaleDateString('fr-FR', { 
                              day: 'numeric', 
                              month: 'short', 
                              year: 'numeric' 
                            })}
                          </span>
                          <select
                            value={transaction.budget_item_id || ''}
                            onChange={async (e) => {
                              const newBudgetItemId = e.target.value ? parseInt(e.target.value) : null;
                              try {
                                setUpdatingTransaction(transaction.id);
                                await budgetAPI.updateTransaction(transaction.id, {
                                  budget_item_id: newBudgetItemId
                                });
                                await loadTransactions();
                                await loadBudgetItems();
                                await loadAnalysisData(); // Reload category chart after category update
                              } catch (error) {
                                console.error('Error updating transaction category:', error);
                                alert('Error updating category: ' + (error.response?.data?.detail || error.message));
                              } finally {
                                setUpdatingTransaction(null);
                              }
                            }}
                            disabled={isUpdating}
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              border: '1px solid var(--border)',
                              backgroundColor: 'var(--bg-primary)',
                              color: 'var(--text-primary)',
                              fontSize: '0.875rem',
                              cursor: isUpdating ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <option value="">-- Uncategorized --</option>
                            {budgetItems.map(item => (
                              <option key={item.id} value={item.id}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                          {budgetItem && (
                            <span className="transaction-category">
                              {budgetItem.label}
                            </span>
                          )}
                          {transaction.bank_statement_id && (
                            <span className="transaction-source">From Bank Statement</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                });
                })()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeAnalysisTab === 'monthly' && (
            <div className="analysis-content">
              <MonthlySummary data={monthlyData} loading={analysisLoading.monthly} />
            </div>
          )}
        </div>
      )}

      {/* Summary Section */}
      {activeSection === 'summary' && summary && (
        <div className="budget-section">
          <h3>Budget Summary</h3>
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-label">Total Charges</div>
              <div className="summary-value">{formatCurrency(summary.total_charges)}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Loans</div>
              <div className="summary-value">{formatCurrency(summary.total_loans)}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Savings</div>
              <div className="summary-value">{formatCurrency(summary.total_savings)}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Monthly</div>
              <div className="summary-value">{formatCurrency(summary.total_monthly)}</div>
            </div>
          </div>

          <h4>Per-Person Calculations</h4>
          <div className="people-calculations">
            {summary.person_calculations.map((calc) => (
              <div key={calc.person_id} className="person-calculation-card">
                <div className="person-name">{calc.person_name}</div>
                <div className="calculation-details">
                  <div className="calc-row">
                    <span className="calc-label">Salary:</span>
                    <span className="calc-value">{formatCurrency(calc.salary)}</span>
                  </div>
                  <div className="calc-row">
                    <span className="calc-label">Charges:</span>
                    <span className="calc-value">{formatCurrency(calc.charge_amount)}</span>
                  </div>
                  <div className="calc-row highlight">
                    <span className="calc-label">Loans:</span>
                    <span className="calc-value">{formatCurrency(calc.loan_amount)}</span>
                  </div>
                  <div className="calc-row">
                    <span className="calc-label">Savings:</span>
                    <span className="calc-value">{formatCurrency(calc.savings_amount)}</span>
                  </div>
                  <div className="calc-row total-deductions">
                    <span className="calc-label">Total Deductions:</span>
                    <span className="calc-value">{formatCurrency(calc.total_deductions)}</span>
                  </div>
                  <div className={`calc-row remaining ${calc.remaining >= 0 ? 'positive' : 'negative'}`}>
                    <span className="calc-label">Remaining:</span>
                    <span className="calc-value">{formatCurrency(calc.remaining)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Report Section */}
      {activeSection === 'report' && (
        <div className="budget-section">
          <h3>Budget vs Actual Report</h3>
          
          <div className="report-filters">
            <div className="filter-group">
              <label>Start Date:</label>
              <input
                type="date"
                value={reportDates.start_date}
                onChange={(e) => setReportDates({ ...reportDates, start_date: e.target.value })}
              />
            </div>
            <div className="filter-group">
              <label>End Date:</label>
              <input
                type="date"
                value={reportDates.end_date}
                onChange={(e) => setReportDates({ ...reportDates, end_date: e.target.value })}
              />
            </div>
            <button
              className="btn-submit"
              onClick={loadReport}
              disabled={reportLoading}
            >
              {reportLoading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>

          {report && (
            <div className="report-results">
              <div className="report-summary-grid">
                <div className="report-summary-card">
                  <div className="report-summary-label">Budgeted Expenses</div>
                  <div className="report-summary-value">{formatCurrency(report.budgeted_expenses)}</div>
                </div>
                <div className="report-summary-card">
                  <div className="report-summary-label">Budgeted Income</div>
                  <div className="report-summary-value">{formatCurrency(report.budgeted_income)}</div>
                </div>
                <div className="report-summary-card future">
                  <div className="report-summary-label">Future Savings</div>
                  <div className="report-summary-value">{formatCurrency(report.future_savings)}</div>
                </div>
                <div className="report-summary-card future">
                  <div className="report-summary-label">Future Loans</div>
                  <div className="report-summary-value">{formatCurrency(report.future_loans)}</div>
                </div>
                <div className={`report-summary-card ${(report.budgeted_income - report.budgeted_expenses - report.future_savings - report.future_loans) >= 0 ? 'positive' : 'negative'}`}>
                  <div className="report-summary-label">Net Balance</div>
                  <div className="report-summary-value">
                    {formatCurrency(report.budgeted_income - report.budgeted_expenses - report.future_savings - report.future_loans)}
                  </div>
                </div>
              </div>

              <h4>Budget Items Breakdown</h4>
              <div className="report-items">
                {report.items && report.items.length > 0 ? (
                  report.items.map((item, index) => (
                    <div key={index} className="report-item-card">
                      <div className="report-item-label">{item.label}</div>
                      <div className="report-item-details">
                        <div>Budgeted: {formatCurrency(item.budgeted)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">No items in this period</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BudgetTab;
